import { normalizeToneModulation, normalizeGranular, normalizeSpectral } from './advancedSynthesisSchema';
import { normalizeWavetable } from './wavetableSchema';
import { normalizeAfm } from './afmSchema';
import { normalizeCorePatch } from './synthCoreSchema';
import { normalizeArpRate } from './arpTiming';
import { normalizeArpPattern } from './arpeggiator';

export const INSTRUMENT_PATCH_VERSION = 6;

export const ENGINE_TYPES = ['core', 'analog', 'wavetable', 'spectral', 'granular', 'fm', 'afm', 'additive', 'texture', 'resonator', 'physical', 'sample'];
export const PHYSICAL_MODELS = ['piano', 'pluck', 'mallet', 'bell', 'bowed', 'wind', 'reed', 'brass'];
export const ACOUSTIC_ARTICULATIONS = ['natural', 'soft', 'bright', 'muted', 'sustain'];
export const OSCILLATOR_WAVEFORMS = ['sine', 'triangle', 'square', 'sawtooth'];
export const FILTER_TYPES = ['lowpass', 'highpass', 'bandpass', 'notch', 'allpass'];
export const LFO_SHAPES = OSCILLATOR_WAVEFORMS;
export const MODULATION_DESTINATIONS = ['pitch', 'filter', 'amplitude', 'pan'];

export const MACRO_NAMES = [
  'Punch', 'Metal', 'Dirt', 'Width', 'Motion', 'Air', 'Body', 'Space'
];

const clamp = (value, min, max, fallback = min) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(min, Math.min(max, numeric));
};

const normalizeSampleZone = (zone, index) => {
  const noteA = Math.round(clamp(zone?.loNote, 0, 127, 0));
  const noteB = Math.round(clamp(zone?.hiNote, 0, 127, 127));
  const velocityA = clamp(zone?.loVelocity, 0, 1, 0);
  const velocityB = clamp(zone?.hiVelocity, 0, 1, 1);
  return {
    id: String(zone?.id || `zone-${index + 1}`).slice(0, 120),
    bufferId: String(zone?.bufferId || zone?.url || '').slice(0, 500),
    url: zone?.url ? String(zone.url).slice(0, 2000) : '',
    // A signed stock URL is deliberately not the durable identity of a zone.
    // Keep the asset reference in the patch so a reopened/shared session can
    // resolve a fresh URL and load the same instrument again.
    sourceType: zone?.sourceType === 'stock' ? 'stock' : (zone?.sourceType === 'user' ? 'user' : ''),
    assetId: zone?.assetId ? String(zone.assetId).slice(0, 160) : '',
    storageKey: zone?.storageKey ? String(zone.storageKey).slice(0, 1000) : '',
    assetVersion: Math.max(1, Math.round(clamp(zone?.assetVersion, 1, 1000000, 1))),
    name: String(zone?.name || `Sample ${index + 1}`).slice(0, 160),
    rootNote: Math.round(clamp(zone?.rootNote, 0, 127, 72)),
    loNote: Math.min(noteA, noteB),
    hiNote: Math.max(noteA, noteB),
    loVelocity: Math.min(velocityA, velocityB),
    hiVelocity: Math.max(velocityA, velocityB),
    gain: clamp(zone?.gain, 0, 4, 1),
    pan: clamp(zone?.pan, -1, 1, 0),
    tune: clamp(zone?.tune, -1200, 1200, 0),
    start: clamp(zone?.start, 0, 3600, 0),
    end: zone?.end == null ? null : clamp(zone.end, 0, 3600, 0),
    loopEnabled: zone?.loopEnabled === true,
    loopStart: clamp(zone?.loopStart, 0, 3600, 0),
    loopEnd: clamp(zone?.loopEnd, 0, 3600, 0),
    loopCrossfade: clamp(zone?.loopCrossfade, 0, 2, 0.02),
    trigger: ['release', 'pedal-down', 'pedal-up'].includes(zone?.trigger) ? zone.trigger : 'attack',
    roundRobin: Math.round(clamp(zone?.roundRobin, 0, 63, 0)),
    // SFZ-imported zones: how far pitch follows the key (0 for hammer and key noise),
    // amplitude velocity tracking (null: CookUp's linear velocity), the zone's own release
    // time (null: the patch's), and how much a release sample fades per second the key was held.
    pitchKeytrack: clamp(zone?.pitchKeytrack, 0, 1, 1),
    velocityTrack: zone?.velocityTrack == null ? null : clamp(zone.velocityTrack, -1, 1, 1),
    release: zone?.release == null ? null : clamp(zone.release, 0, 60, 0),
    releaseDecay: clamp(zone?.releaseDecay, 0, 60, 0)
  };
};

const LIBRARY_ID = /^[a-z0-9][a-z0-9-]{0,59}$/;

/** A CookUp-hosted multisampled instrument (hostedLibraries.js), or null. */
const normalizeHostedInstrument = (value) => (
  value && LIBRARY_ID.test(String(value.library || '')) && LIBRARY_ID.test(String(value.instrument || ''))
    ? { library: String(value.library), instrument: String(value.instrument) }
    : null
);

const createLayer = (id, engine, name, color, values = {}) => ({
  id,
  enabled: true,
  engine,
  name,
  color,
  waveform: engine === 'fm' ? 'sine' : engine === 'additive' ? 'triangle' : 'sawtooth',
  octave: 0,
  semitone: 0,
  detune: 0,
  level: 0.5,
  pan: 0,
  tone: 0.65,
  width: 0.45,
  ratio: 2,
  index: 2.5,
  feedback: 0,
  density: 0.45,
  size: 0.5,
  harmonics: [1, 0.56, 0.34, 0.22, 0.15, 0.1, 0.07, 0.05, 0, 0, 0, 0, 0, 0, 0, 0],
  harmonicTilt: 0.5,
  inharmonicity: 0,
  damping: 0.55,
  physicalModel: 'piano',
  exciter: 0.55,
  bodySize: 0.5,
  bodyDecay: 0.62,
  material: 0.45,
  pickPosition: 0.28,
  breath: 0.12,
  articulation: 'natural',
  bodyMix: 0.68,
  sympathetic: 0.35,
  coupling: 0.42,
  mechanics: 0.18,
  variation: 0.2,
  micPosition: 0.28,
  noiseColor: 'pink',
  sampleMode: 'gate',
  sampleStart: 0,
  sampleEnd: null,
  loopEnabled: false,
  loopStart: 0,
  loopEnd: 0,
  loopCrossfade: 0.02,
  velocityTone: 0.6,
  humanize: 0.5,
  sampleZones: [],
  hostedInstrument: null,
  unison: engine === 'analog' ? 2 : 1,
  envelope: { enabled: false, attack: 0.001, decay: 0.2, sustain: 1, release: 0.2 },
  ...values
});

export const createDefaultInstrumentPatch = () => ({
  version: INSTRUMENT_PATCH_VERSION,
  id: 'dark-brass-bass',
  name: 'Dark Brass Bass',
  category: 'Bass',
  tags: ['Dark', 'Hybrid'],
  favorite: false,
  macros: {
    Punch: 0.68,
    Metal: 0.42,
    Dirt: 0.32,
    Width: 0.55,
    Motion: 0.28,
    Air: 0.25,
    Body: 0.72,
    Space: 0.18
  },
  layers: [
    createLayer('layer-1', 'analog', 'Detuned Brass', '#a966ff', {
      waveform: 'sawtooth', level: 0.58, detune: 12, tone: 0.56, width: 0.62
    }),
    createLayer('layer-2', 'fm', 'Metallic Transient', '#4a9dff', {
      level: 0.22, ratio: 2, index: 3.2, tone: 0.72
    }),
    createLayer('layer-3', 'additive', 'Harmonic Stack', '#ffb345', {
      level: 0.32, tone: 0.48, width: 0.3
    }),
    createLayer('layer-4', 'texture', 'Noisy Attack', '#40d8d1', {
      level: 0.13, density: 0.35, size: 0.28, tone: 0.76
    })
  ],
  ampEnvelope: { attack: 0.012, decay: 0.34, sustain: 0.68, release: 0.48, velocity: 0.75 },
  filterEnvelope: { attack: 0.005, decay: 0.48, sustain: 0.25, release: 0.35, amount: 0.62 },
  filter: { enabled: true, type: 'lowpass', cutoff: 2700, resonance: 1.15, drive: 0.18, keyTrack: 0.35 },
  filter2: { enabled: false, type: 'highpass', cutoff: 55, resonance: 0.7 },
  lfo: { enabled: true, shape: 'sine', rate: 0.32, depth: 0.08, sync: false, destination: 'filter' },
  lfo2: { enabled: false, shape: 'triangle', rate: 2, depth: 0.12, sync: true, destination: 'pitch' },
  stepMod: { enabled: false, rate: 0.25, depth: 0.3, steps: [0.2, 0.5, 0.35, 0.8, 0.25, 0.65, 0.4, 0.9] },
  arp: { enabled: false, rate: 0.25, octaves: 1, gate: 0.72, ...normalizeArpPattern() },
  effects: {
    distortion: { enabled: true, drive: 0.16, mix: 0.2 },
    chorus: { enabled: true, rate: 0.24, depth: 0.28, mix: 0.18 },
    delay: { enabled: false, time: 0.25, feedback: 0.28, mix: 0.12 },
    reverb: { enabled: true, size: 0.42, damping: 0.55, mix: 0.12 },
    compressor: { enabled: true, threshold: -15, ratio: 3.5, attack: 0.008, release: 0.16 }
  },
  performance: { master: 0.72, glide: 0.018, voices: 16, pitchBendRange: 2, mono: false, legato: false }
});

const normalizeLayer = (layer, index, fallback) => ({
  ...fallback,
  ...(layer || {}),
  id: String(layer?.id || fallback.id || `layer-${index + 1}`),
  engine: ENGINE_TYPES.includes(layer?.engine) ? layer.engine : fallback.engine,
  enabled: layer?.enabled !== false,
  octave: Math.round(clamp(layer?.octave, -3, 3, fallback.octave)),
  semitone: Math.round(clamp(layer?.semitone, -12, 12, fallback.semitone)),
  detune: clamp(layer?.detune, -100, 100, fallback.detune),
  level: clamp(layer?.level, 0, 1, fallback.level),
  pan: clamp(layer?.pan, -1, 1, fallback.pan),
  tone: clamp(layer?.tone, 0, 1, fallback.tone),
  width: clamp(layer?.width, 0, 1, fallback.width),
  afm: normalizeAfm(layer?.afm),
  // Only Core layers carry (and persist) a Core patch.
  core: layer?.engine === 'core' || layer?.core ? normalizeCorePatch(layer?.core) : undefined,
  wavetable: ['wavetable','spectral'].includes(layer?.engine) || layer?.wavetable ? normalizeWavetable(layer?.wavetable) : undefined,
  granular: layer?.engine === 'granular' || layer?.granular ? normalizeGranular(layer?.granular) : undefined,
  spectral: layer?.engine === 'spectral' || layer?.spectral ? normalizeSpectral(layer?.spectral) : undefined,
  toneModulation: ['wavetable','spectral','granular'].includes(layer?.engine) || layer?.toneModulation ? normalizeToneModulation(layer?.toneModulation) : undefined,
  ratio: clamp(layer?.ratio, 0.25, 16, fallback.ratio),
  index: clamp(layer?.index, 0, 16, fallback.index),
  feedback: clamp(layer?.feedback, 0, 1, fallback.feedback),
  density: clamp(layer?.density, 0, 1, fallback.density),
  size: clamp(layer?.size, 0, 1, fallback.size),
  waveform: OSCILLATOR_WAVEFORMS.includes(layer?.waveform) ? layer.waveform : fallback.waveform,
  unison: Math.round(clamp(layer?.unison, 1, 8, fallback.unison || 1)),
  harmonics: Array.from({ length: 16 }, (_, harmonicIndex) => (
    clamp(layer?.harmonics?.[harmonicIndex], 0, 1, fallback.harmonics?.[harmonicIndex] || 0)
  )),
  harmonicTilt: clamp(layer?.harmonicTilt, 0, 1, fallback.harmonicTilt || 0.5),
  inharmonicity: clamp(layer?.inharmonicity, 0, 1, fallback.inharmonicity || 0),
  damping: clamp(layer?.damping, 0, 1, fallback.damping || 0.55),
  physicalModel: PHYSICAL_MODELS.includes(layer?.physicalModel) ? layer.physicalModel : fallback.physicalModel,
  exciter: clamp(layer?.exciter, 0, 1, fallback.exciter || 0.55),
  bodySize: clamp(layer?.bodySize, 0, 1, fallback.bodySize || 0.5),
  bodyDecay: clamp(layer?.bodyDecay, 0, 1, fallback.bodyDecay || 0.62),
  material: clamp(layer?.material, 0, 1, fallback.material || 0.45),
  pickPosition: clamp(layer?.pickPosition, 0.02, 0.98, fallback.pickPosition || 0.28),
  breath: clamp(layer?.breath, 0, 1, fallback.breath || 0.12),
  articulation: ACOUSTIC_ARTICULATIONS.includes(layer?.articulation) ? layer.articulation : fallback.articulation,
  bodyMix: clamp(layer?.bodyMix, 0, 1, fallback.bodyMix ?? 0.68),
  sympathetic: clamp(layer?.sympathetic, 0, 1, fallback.sympathetic ?? 0.35),
  coupling: clamp(layer?.coupling, 0, 1, fallback.coupling ?? 0.42),
  mechanics: clamp(layer?.mechanics, 0, 1, fallback.mechanics ?? 0.18),
  variation: clamp(layer?.variation, 0, 1, fallback.variation ?? 0.2),
  micPosition: clamp(layer?.micPosition, 0, 1, fallback.micPosition ?? 0.28),
  noiseColor: ['white', 'pink', 'brown'].includes(layer?.noiseColor) ? layer.noiseColor : fallback.noiseColor,
  sampleMode: layer?.sampleMode === 'one-shot' ? 'one-shot' : 'gate',
  sampleStart: clamp(layer?.sampleStart, 0, 3600, fallback.sampleStart || 0),
  sampleEnd: layer?.sampleEnd == null ? null : clamp(layer.sampleEnd, 0, 3600, 0),
  loopEnabled: layer?.loopEnabled === true,
  loopStart: clamp(layer?.loopStart, 0, 3600, fallback.loopStart || 0),
  loopEnd: clamp(layer?.loopEnd, 0, 3600, fallback.loopEnd || 0),
  loopCrossfade: clamp(layer?.loopCrossfade, 0, 2, fallback.loopCrossfade || 0.02),
  // Soft notes darker as well as quieter (sample layers without velocity layers); 0 turns it off.
  velocityTone: clamp(layer?.velocityTone, 0, 1, fallback.velocityTone ?? 0.6),
  // Round-robin emulation for single recordings: slight per-play pitch, level and tone differences; 0 turns it off.
  humanize: clamp(layer?.humanize, 0, 1, fallback.humanize ?? 0.5),
  sampleZones: Array.isArray(layer?.sampleZones)
    ? layer.sampleZones.slice(0, 1024).map(normalizeSampleZone)
    : [],
  // Zones streamed from a hosted library instead of stored in the patch.
  hostedInstrument: normalizeHostedInstrument(layer?.hostedInstrument),
  envelope: {
    ...fallback.envelope,
    ...(layer?.envelope || {}),
    enabled: layer?.envelope?.enabled === true,
    attack: clamp(layer?.envelope?.attack, 0.001, 8, fallback.envelope.attack),
    decay: clamp(layer?.envelope?.decay, 0.001, 8, fallback.envelope.decay),
    sustain: clamp(layer?.envelope?.sustain, 0, 1, fallback.envelope.sustain),
    release: clamp(layer?.envelope?.release, 0.005, 12, fallback.envelope.release)
  }
});

const normalizeLfo = (candidate, fallback) => ({
  ...fallback,
  ...(candidate || {}),
  enabled: candidate?.enabled == null ? fallback.enabled === true : candidate.enabled === true,
  shape: LFO_SHAPES.includes(candidate?.shape) ? candidate.shape : fallback.shape,
  rate: clamp(candidate?.rate, 0.02, 30, fallback.rate),
  depth: clamp(candidate?.depth, 0, 1, fallback.depth),
  sync: candidate?.sync == null ? fallback.sync === true : candidate.sync === true,
  destination: MODULATION_DESTINATIONS.includes(candidate?.destination)
    ? candidate.destination
    : fallback.destination
});

const normalizeFilter = (candidate, fallback, includeDrive = false) => ({
  ...fallback,
  ...(candidate || {}),
  enabled: candidate?.enabled == null ? fallback.enabled !== false : candidate.enabled !== false,
  type: FILTER_TYPES.includes(candidate?.type) ? candidate.type : fallback.type,
  cutoff: clamp(candidate?.cutoff, 20, 20000, fallback.cutoff),
  resonance: clamp(candidate?.resonance, 0.0001, 24, fallback.resonance),
  ...(includeDrive ? {
    drive: clamp(candidate?.drive, 0, 1, fallback.drive),
    keyTrack: clamp(candidate?.keyTrack, 0, 1, fallback.keyTrack)
  } : {})
});

export const normalizeInstrumentPatch = (candidate = {}) => {
  const base = createDefaultInstrumentPatch();
  const patch = { ...base, ...(candidate || {}) };
  const layers = Array.from({ length: 4 }, (_, index) => (
    normalizeLayer(candidate?.layers?.[index], index, base.layers[index])
  ));
  const macros = MACRO_NAMES.reduce((result, name) => {
    result[name] = clamp(candidate?.macros?.[name], 0, 1, base.macros[name]);
    return result;
  }, {});

  return {
    ...patch,
    version: INSTRUMENT_PATCH_VERSION,
    id: String(candidate?.id || base.id),
    name: String(candidate?.name || base.name).slice(0, 80),
    category: String(candidate?.category || base.category).slice(0, 40),
    tags: Array.isArray(candidate?.tags)
      ? candidate.tags.slice(0, 12).map((tag) => String(tag).slice(0, 30))
      : base.tags,
    favorite: candidate?.favorite === true,
    // Attribution a hosted library's licence asks for, shown wherever the instrument is.
    ...(candidate?.credit ? { credit: String(candidate.credit).slice(0, 300) } : {}),
    macros,
    layers,
    ampEnvelope: {
      attack: clamp(candidate?.ampEnvelope?.attack, 0.001, 8, base.ampEnvelope.attack),
      decay: clamp(candidate?.ampEnvelope?.decay, 0.001, 8, base.ampEnvelope.decay),
      sustain: clamp(candidate?.ampEnvelope?.sustain, 0, 1, base.ampEnvelope.sustain),
      release: clamp(candidate?.ampEnvelope?.release, 0.005, 12, base.ampEnvelope.release),
      velocity: clamp(candidate?.ampEnvelope?.velocity, 0, 1, base.ampEnvelope.velocity)
    },
    filterEnvelope: {
      attack: clamp(candidate?.filterEnvelope?.attack, 0.001, 8, base.filterEnvelope.attack),
      decay: clamp(candidate?.filterEnvelope?.decay, 0.001, 8, base.filterEnvelope.decay),
      sustain: clamp(candidate?.filterEnvelope?.sustain, 0, 1, base.filterEnvelope.sustain),
      release: clamp(candidate?.filterEnvelope?.release, 0.005, 12, base.filterEnvelope.release),
      amount: clamp(candidate?.filterEnvelope?.amount, -1, 1, base.filterEnvelope.amount)
    },
    filter: normalizeFilter(candidate?.filter, base.filter, true),
    filter2: normalizeFilter(candidate?.filter2, base.filter2),
    performance: {
      ...base.performance,
      ...(candidate?.performance || {}),
      master: clamp(candidate?.performance?.master, 0, 1, base.performance.master),
      glide: clamp(candidate?.performance?.glide, 0, 2, base.performance.glide),
      voices: Math.round(clamp(candidate?.performance?.voices, 8, 32, base.performance.voices)),
      pitchBendRange: Math.round(clamp(candidate?.performance?.pitchBendRange, 1, 24, base.performance.pitchBendRange)),
      // CookUp instruments are always chord-capable. Legacy/imported mono
      // patches are upgraded here so every path through the app stays poly.
      mono: false,
      legato: candidate?.performance?.legato === true
    },
    lfo: normalizeLfo(candidate?.lfo, base.lfo),
    lfo2: normalizeLfo(candidate?.lfo2, base.lfo2),
    stepMod: {
      ...base.stepMod,
      ...(candidate?.stepMod || {}),
      enabled: candidate?.stepMod?.enabled === true,
      rate: clamp(candidate?.stepMod?.rate, 0.0625, 4, base.stepMod.rate),
      depth: clamp(candidate?.stepMod?.depth, 0, 1, base.stepMod.depth),
      steps: Array.from({ length: 8 }, (_, index) => (
        clamp(candidate?.stepMod?.steps?.[index], 0, 1, base.stepMod.steps[index])
      ))
    },
    arp: {
      ...base.arp,
      ...(candidate?.arp || {}),
      ...normalizeArpPattern(candidate?.arp || {}),
      enabled: candidate?.arp?.enabled === true,
      rate: normalizeArpRate(candidate?.arp?.rate, base.arp.rate),
      octaves: Math.round(clamp(candidate?.arp?.octaves, 1, 4, base.arp.octaves)),
      gate: clamp(candidate?.arp?.gate, 0.05, 1, base.arp.gate)
    },
    effects: {
      ...base.effects,
      ...(candidate?.effects || {}),
      distortion: {
        ...base.effects.distortion, ...(candidate?.effects?.distortion || {}),
        enabled: candidate?.effects?.distortion?.enabled == null ? base.effects.distortion.enabled : candidate.effects.distortion.enabled === true,
        drive: clamp(candidate?.effects?.distortion?.drive, 0, 1, base.effects.distortion.drive),
        mix: clamp(candidate?.effects?.distortion?.mix, 0, 1, base.effects.distortion.mix)
      },
      chorus: {
        ...base.effects.chorus, ...(candidate?.effects?.chorus || {}),
        enabled: candidate?.effects?.chorus?.enabled == null ? base.effects.chorus.enabled : candidate.effects.chorus.enabled === true,
        rate: clamp(candidate?.effects?.chorus?.rate, 0.01, 10, base.effects.chorus.rate),
        depth: clamp(candidate?.effects?.chorus?.depth, 0, 1, base.effects.chorus.depth),
        mix: clamp(candidate?.effects?.chorus?.mix, 0, 1, base.effects.chorus.mix)
      },
      delay: {
        ...base.effects.delay, ...(candidate?.effects?.delay || {}),
        enabled: candidate?.effects?.delay?.enabled == null ? base.effects.delay.enabled : candidate.effects.delay.enabled === true,
        time: clamp(candidate?.effects?.delay?.time, 0.01, 2, base.effects.delay.time),
        feedback: clamp(candidate?.effects?.delay?.feedback, 0, 0.92, base.effects.delay.feedback),
        mix: clamp(candidate?.effects?.delay?.mix, 0, 1, base.effects.delay.mix)
      },
      reverb: {
        ...base.effects.reverb, ...(candidate?.effects?.reverb || {}),
        enabled: candidate?.effects?.reverb?.enabled == null ? base.effects.reverb.enabled : candidate.effects.reverb.enabled === true,
        size: clamp(candidate?.effects?.reverb?.size, 0, 1, base.effects.reverb.size),
        damping: clamp(candidate?.effects?.reverb?.damping, 0, 1, base.effects.reverb.damping),
        mix: clamp(candidate?.effects?.reverb?.mix, 0, 1, base.effects.reverb.mix)
      },
      compressor: {
        ...base.effects.compressor, ...(candidate?.effects?.compressor || {}),
        enabled: candidate?.effects?.compressor?.enabled == null ? base.effects.compressor.enabled : candidate.effects.compressor.enabled === true,
        threshold: clamp(candidate?.effects?.compressor?.threshold, -100, 0, base.effects.compressor.threshold),
        ratio: clamp(candidate?.effects?.compressor?.ratio, 1, 20, base.effects.compressor.ratio),
        attack: clamp(candidate?.effects?.compressor?.attack, 0, 1, base.effects.compressor.attack),
        release: clamp(candidate?.effects?.compressor?.release, 0.01, 1, base.effects.compressor.release)
      }
    }
  };
};

export const cloneInstrumentPatch = (patch) => normalizeInstrumentPatch(
  JSON.parse(JSON.stringify(patch || createDefaultInstrumentPatch()))
);

export { clamp };
